import React from 'react';
import {NotificationItem} from '../store/NotificationStore';
import {formatRelative} from '../utils/dateTime';

/**
 * Человекочитаемое относительное время создания уведомления (ru). Свежие — «только что / N мин / N ч»,
 * старые — короткая дата. Логика — в `utils/dateTime.formatRelative` (строка без смещения = UTC,
 * R1/U7); здесь тонкая обёртка ради существующих импортов выпадашки и страницы «Все уведомления».
 */
export const formatNotificationTime = (iso?: string): string => formatRelative(iso);

/**
 * Цвет акцента непрочитанного по типу уведомления. Возвращает полные литералы Tailwind-классов
 * (иначе purge их вырежет). Статус передаётся не только цветом: непрочитанность дублируется
 * жирностью заголовка и точкой-индикатором (styleguide: «never color alone»).
 */
function unreadDotClass(type?: string): string {
    switch ((type || '').toLowerCase()) {
        case 'alert':
            return 'bg-red-500';
        case 'warn':
            return 'bg-amber-500';
        case 'autopilot':
            return 'bg-brand-500';
        default:
            return 'bg-primary-500';
    }
}

interface NotificationItemRowProps {
    item: NotificationItem;
    onActivate: (item: NotificationItem) => void;
}

/**
 * Одна строка уведомления — переиспользуется в выпадашке колокольчика и на странице.
 * Кнопка во всю ширину с тач-зоной ≥44px; цвета — семантические Tailwind-токены с dark:-вариантами.
 */
const NotificationItemRow: React.FC<NotificationItemRowProps> = ({item, onActivate}) => {
    return (
        <button
            type="button"
            onClick={() => onActivate(item)}
            data-testid="notification-row"
            data-read={item.read ? 'true' : 'false'}
            className={`w-full text-left flex gap-3 px-4 py-3 min-h-[44px] transition-colors
                hover:bg-gray-50 dark:hover:bg-white/5
                focus:outline-none focus:bg-gray-50 dark:focus:bg-white/5
                ${item.read ? '' : 'bg-primary-50/70 dark:bg-white/[0.04]'}`}
        >
            <span className="mt-1.5 shrink-0">
                {item.read ? (
                    <span className="block w-2 h-2" aria-hidden="true"/>
                ) : (
                    <span
                        className={`block w-2 h-2 rounded-full ${unreadDotClass(item.type)}`}
                        aria-label="непрочитано"
                    />
                )}
            </span>
            <span className="min-w-0 flex-1">
                <span
                    className={`block text-sm truncate ${
                        item.read
                            ? 'font-medium text-gray-700 dark:text-gray-300'
                            : 'font-semibold text-gray-900 dark:text-gray-100'
                    }`}
                >
                    {item.title}
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                    {item.text}
                </span>
                <span className="block text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                    {formatNotificationTime(item.createdAt)}
                </span>
            </span>
        </button>
    );
};

export default NotificationItemRow;
